"use client";

import React, { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import { ExclamationCircleFilled, DesktopOutlined } from "@ant-design/icons";

export function MultiTabDetector() {
  const [showConflict, setShowConflict] = useState(false);
  const [conflictingTabs, setConflictingTabs] = useState([]);
  const [currentTabId] = useState(() => `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    const registerTab = () => {
      const existingTabs = JSON.parse(localStorage.getItem("pos_active_tabs") || "[]");
      const updatedTabs = [
        ...existingTabs,
        {
          id: currentTabId,
          timestamp: Date.now(),
          url: window.location.href,
        },
      ];
      localStorage.setItem("pos_active_tabs", JSON.stringify(updatedTabs));
      sessionStorage.setItem("pos_tab_id", currentTabId);
    };

    const checkExistingTabs = () => {
      const existingTabs = JSON.parse(localStorage.getItem("pos_active_tabs") || "[]");
      const activeTabs = existingTabs.filter((tab) => Date.now() - tab.timestamp < 30000);

      if (activeTabs.length > 1) {
        setConflictingTabs(activeTabs.filter((tab) => tab.id !== currentTabId));
        setShowConflict(true);
      }
    };

    const updateHeartbeat = () => {
      const existingTabs = JSON.parse(localStorage.getItem("pos_active_tabs") || "[]");
      const updatedTabs = existingTabs.map((tab) => (tab.id === currentTabId ? { ...tab, timestamp: Date.now() } : tab));
      localStorage.setItem("pos_active_tabs", JSON.stringify(updatedTabs));
    };

    const handleStorageChange = (e) => {
      if (e.key === "pos_active_tabs") {
        const tabs = JSON.parse(e.newValue || "[]");
        const otherActiveTabs = tabs.filter((tab) => tab.id !== currentTabId && Date.now() - tab.timestamp < 30000);

        if (otherActiveTabs.length > 0) {
          setConflictingTabs(otherActiveTabs);
          setShowConflict(true);
        } else {
          setShowConflict(false);
          setConflictingTabs([]);
        }
      }
    };

    const handleBeforeUnload = () => {
      const existingTabs = JSON.parse(localStorage.getItem("pos_active_tabs") || "[]");
      const updatedTabs = existingTabs.filter((tab) => tab.id !== currentTabId);
      localStorage.setItem("pos_active_tabs", JSON.stringify(updatedTabs));
    };

    checkExistingTabs();
    registerTab();

    const heartbeatInterval = setInterval(updateHeartbeat, 5000);
    const cleanupInterval = setInterval(() => {
      const existingTabs = JSON.parse(localStorage.getItem("pos_active_tabs") || "[]");
      const activeTabs = existingTabs.filter((tab) => Date.now() - tab.timestamp < 30000);
      localStorage.setItem("pos_active_tabs", JSON.stringify(activeTabs));
    }, 10000);

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(cleanupInterval);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      handleBeforeUnload();
    };
  }, [currentTabId]);

  const handleTakeOverTab = () => {
    const newTabs = [
      {
        id: currentTabId,
        timestamp: Date.now(),
        url: window.location.href,
      },
    ];
    localStorage.setItem("pos_active_tabs", JSON.stringify(newTabs));
    setShowConflict(false);
    setConflictingTabs([]);
    window.location.reload();
  };

  if (!showConflict) return null;

  return (
    <Modal
      visible={true}
      centered
      closable={false}
      footer={null}
      maskClosable={false}
      width={400}
      bodyStyle={{
        padding: 24,
        borderRadius: 8,
      }}
      style={{
        borderRadius: 8,
      }}
    >
      <div style={{ textAlign: "center" }}>
        {/* Icon Circle */}
        <div
          style={{
            width: 48,
            height: 48,
            background: "#FFF4E5",
            borderRadius: "50%",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          <ExclamationCircleFilled style={{ color: "#FFA126", fontSize: 24 }} />
        </div>

        {/* Title */}
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          POS Already Open
        </div>

        {/* Description */}
        <div
          style={{
            fontSize: 14,
            color: "#6B7280",
            marginBottom: 24,
          }}
        >
          POS is already running in another tab. To protect your data and prevent conflicts, you can only use POS in one tab at a time.
        </div>

        {/* Active Sessions Box */}
        <div
          style={{
            background: "#F9FAFB",
            padding: 16,
            borderRadius: 8,
            marginBottom: 24,
            textAlign: "left",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "#6B7280",
              fontSize: 14,
              marginBottom: 12,
              fontWeight: 500,
            }}
          >
            <DesktopOutlined style={{ fontSize: 16 }} />
            Active Sessions
          </div>

          {conflictingTabs.map((tab, index) => (
            <div
              key={tab.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 14,
                marginBottom: 8,
              }}
            >
              <span>Tab {index + 1}</span>
              <span
                style={{
                  background: "#ECFDF5",
                  color: "#065F46",
                  fontSize: 12,
                  fontWeight: 500,
                  padding: "2px 8px",
                  borderRadius: 12,
                }}
              >
                Active
              </span>
            </div>
          ))}

          <div
            style={{
              borderTop: "1px solid #E5E7EB",
              paddingTop: 8,
              marginTop: 8,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            <span>Current Tab</span>
            <span
              style={{
                background: "#F3F4F6",
                color: "#374151",
                fontSize: 12,
                fontWeight: 500,
                padding: "2px 8px",
                borderRadius: 12,
              }}
            >
              Waiting
            </span>
          </div>
        </div>

        {/* Button */}
        <Button
          type="primary"
          style={{
            background: "#111827",
            borderColor: "#111827",
            height: 40,
            borderRadius: 6,
            width: "100%",
            fontWeight: 500,
            fontSize: 14,
          }}
          onClick={handleTakeOverTab}
        >
          Use POS Here
        </Button>

        {/* Bottom note */}
        <div
          style={{
            fontSize: 12,
            color: "#6B7280",
            marginTop: 16,
          }}
        >
          This helps prevent data conflicts and ensures smooth operation of your POS system.
        </div>
      </div>
    </Modal>
  );
}
